/**
 * API 客户端基础设施
 * 提供统一的请求封装、错误处理、Token 管理
 */

// API 基础地址
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8082';

// Token 存储键
const TOKEN_KEY = 'myorg_token';

// 统一响应格式
export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

// 分页响应数据
export interface PaginatedData<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// API 错误
export class ApiError extends Error {
  code: number;

  constructor(code: number, message: string) {
    super(message);
    this.code = code;
    this.name = 'ApiError';
  }
}

// Token 管理
export const tokenManager = {
  get: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  set: (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  remove: (): void => {
    localStorage.removeItem(TOKEN_KEY);
  },

  isValid: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;

    try {
      // 解析 JWT payload（不验证签名，仅检查过期时间）
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }
};

// 请求配置
interface RequestConfig extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

// 构建 URL 参数
function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  const url = new URL(`${API_BASE_URL}${path}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

// 基础请求方法
async function request<T>(
  path: string,
  config: RequestConfig = {}
): Promise<T> {
  const { params, ...fetchConfig } = config;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...config.headers,
  };

  // 添加 Token
  const token = tokenManager.get();
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const url = buildUrl(path, params);

  try {
    const response = await fetch(url, {
      ...fetchConfig,
      headers,
    });

    const result: ApiResponse<T> = await response.json();

    // 处理未授权
    if (result.code === 401) {
      tokenManager.remove();
      // 触发登出事件
      window.dispatchEvent(new CustomEvent('auth:logout'));
      throw new ApiError(401, result.message || 'Unauthorized');
    }

    // 处理其他错误
    if (result.code !== 200) {
      throw new ApiError(result.code, result.message);
    }

    return result.data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // 网络错误
    console.error('API request failed:', error);
    throw new ApiError(500, 'Network error');
  }
}

// HTTP 方法封装
export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    request<T>(path, { method: 'GET', params }),

  post: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    }),

  put: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }),

  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
};

// API 路径常量
export const API_PATHS = {
  // Auth
  AUTH_LOGIN: '/api/v1/org/auth/login',
  AUTH_LOGOUT: '/api/v1/org/auth/logout',
  AUTH_ME: '/api/v1/org/auth/me',

  // Users
  USERS: '/api/v1/org/users',
  USERS_BATCH_DELETE: '/api/v1/org/users/batch-delete',
  USERS_IMPORT: '/api/v1/org/users/import',
  USERS_EXPORT: '/api/v1/org/users/export',

  // Groups
  GROUPS: '/api/v1/org/groups',

  // Apps (Admin)
  APPS: '/api/v1/org/apps',

  // Creator
  CREATOR_APPS: '/api/v1/org/creator/apps',
  CREATOR_APPS_SUBMIT: '/api/v1/org/creator/apps/submit',
  CREATOR_HISTORY: '/api/v1/org/creator/history',
  CREATOR_GROUPS: '/api/v1/org/creator/groups',

  // Reviews
  REVIEWS: '/api/v1/org/reviews',

  // Store
  STORE_APPS: '/api/v1/org/store/apps',

  // Settings
  SETTINGS: '/api/v1/org/settings',

  // Tools
  TOOL_AZURE_CREATE_SAS: '/api/v1/org/tool/azureCreateSAS',

  // Dashboard
  DASHBOARD_STATS: '/api/v1/org/dashboard/stats',
} as const;
